import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, api, storageApi, textingApi, transcribed, type CompanySettings, type TextingSettingsView } from '../../api/client';
import { STATE_WORDS } from './TextingRegistration';
import { useTenant } from '../../components/Shell';
import { TimeZoneSelect } from '../../components/TimeZone';
import { Button, Card, Chip, LoadError, Loading, Notice, PageHeader, TextAreaField } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { shortWhen } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { zoneLabel } from '../../lib/time';
import { useData } from '../../lib/useData';
import { RecordingSettingsCard } from './RecordingSettings';
// The call records service: voicemail emails and call recording transcripts.
import { CallRecordsSettingsCards } from './CallRecordsSettings';
import { ReceptionSettingsCard } from '../../components/ReceptionSettings';
import { QueueBackupCard } from '../../components/QueueBackup';
import { MenuZeroCard } from '../../components/MenuZero';
import { MAILBOX_FEATURE_SET } from '@stitch/pbx-adapter/catalog';
// Smart messaging: Company Settings → Messages, and legal holds.
import { LegalHoldsCard, SmartMessagesCard } from '../../components/messages/SmartSettings';
import { MessagesExportCard } from '../../components/messages/ExportCard';

/** The company's own settings in Stitch: its time zone, and session recording. */
export function TenantSettings() {
  const { tenant, reload } = useTenant();
  const { state, refresh } = useAuth();
  // The company's own admins (not Stitch staff looking in): its texting switch (1.12.30).
  const companyAdmin = state?.memberships?.some((m) => m.tenant === tenant.domain.toLowerCase() && m.role === 'admin') ?? false;
  // Stitch's own settings, not the phone system's: never reloaded under someone choosing a zone.
  const settings = useData(() => api.companySettings(tenant.domain), [tenant.domain], { live: false });
  const [mode, setMode] = useState<'default' | 'own'>('default');
  const [zone, setZone] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');

  const s = settings.data;
  // Filled in once per company; after that only by saving.
  const filled = useRef<string | null>(null);
  useEffect(() => {
    if (!s || filled.current === tenant.domain) return;
    filled.current = tenant.domain;
    setMode(s.timeZone ? 'own' : 'default');
    setZone(s.timeZone ?? s.defaultTimeZone);
  }, [s, tenant.domain]);

  async function save() {
    if (!s) return;
    setBusy(true);
    setError('');
    setSaved('');
    try {
      const next = await api.saveCompanySettings(tenant.domain, { timeZone: mode === 'own' ? zone : null });
      setMode(next.timeZone ? 'own' : 'default');
      setZone(next.timeZone ?? next.defaultTimeZone);
      settings.reload();
      // The menu, and every time shown, follow straight away.
      reload();
      await refresh();
      setSaved(`Saved. ${tenant.name}’s business hours, holidays and reports now use ${zoneLabel(next.timeZone ?? next.defaultTimeZone)}.`);
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusy(false);
    }
  }

  const changed = s && (mode === 'own' ? zone !== s.timeZone : s.timeZone !== null);
  return (
    <>
      <PageHeader eyebrow="Company" title="Settings" />
      <div className="content">
        {/* 1.13.0: everything Stitch does for the company, on or not, and asking Stitch for what only it turns on. */}
        <Notice icon="grid" title="What’s on">
          Everything Stitch does for {tenant.name}, whether it’s on, and why not.{' '}
          <Link to={`/t/${encodeURIComponent(tenant.domain)}/whats-on`}>See what’s on</Link>
        </Notice>
        {settings.error ? (
          <Card>
            <LoadError error={settings.error} retry={settings.reload} />
          </Card>
        ) : !s ? (
          <Card>
            <Loading label="Loading settings" />
          </Card>
        ) : (
          <Card title="Time zone">
            <div className="card-body settings-section">
              <p className="muted settings-lede">
                Where {tenant.name} is. Business hours, holidays, “today” in queue reports and the office TV use it, wherever people are. Everyone else sees
                times in their own device’s time zone, unless they choose this one (or another) under <strong>Your settings</strong> at the bottom of the menu.
              </p>
              {!s.editable && <Notice tone="warn">This tenant isn’t a Stitch customer yet, so it has nowhere to keep a time zone. Stitch staff can add it under Customers.</Notice>}
              <fieldset className="plain-fieldset" disabled={!s.editable}>
                <legend className="sr-only">Time zone</legend>
                <label className="radio-card">
                  <input type="radio" name="company-tz" checked={mode === 'default'} onChange={() => setMode('default')} />
                  <span className="cell-stack">
                    <strong>Stitch’s standard time zone</strong>
                    <small>{zoneLabel(s.defaultTimeZone)}</small>
                  </span>
                </label>
                <label className="radio-card">
                  <input type="radio" name="company-tz" checked={mode === 'own'} onChange={() => setMode('own')} />
                  <span className="cell-stack">
                    <strong>Our own time zone</strong>
                    <small>{mode === 'own' && zone ? `${zoneLabel(zone)}: it’s ${clockTimeIn(zone)} there now` : 'Choose one'}</small>
                  </span>
                </label>
                {mode === 'own' && (
                  <TimeZoneSelect
                    id="company-tz"
                    value={zone}
                    onChange={setZone}
                    hint="Use the same time zone as your phone system, so a day in reports matches the phone system’s day."
                  />
                )}
              </fieldset>
              {saved && <Notice tone="ok">{saved}</Notice>}
              {error && <Notice tone="crit">{error}</Notice>}
              <div className="settings-actions">
                <Button variant="primary" disabled={busy || !s.editable || !changed} onClick={() => void save()}>
                  {busy ? 'Saving…' : 'Save time zone'}
                </Button>
              </div>
              <p className="muted small-print">Each person’s own time zone on the phone system (for their voicemail and desk phone clock) is on People: edit the person.</p>
            </div>
          </Card>
        )}
        {s?.editable && s.transcripts && <TranscriptsCard domain={tenant.domain} t={s.transcripts} onSaved={settings.reload} />}
        {s?.editable && <CallRecordsSettingsCards domain={tenant.domain} />}
        {s?.editable && <ReceptionSettingsCard domain={tenant.domain} writable={tenant.writable} />}
        {s?.editable && <QueueBackupCard domain={tenant.domain} writable={tenant.writable} />}
        {s?.editable && <MenuZeroCard domain={tenant.domain} writable={tenant.writable} />}
        {s?.editable && <RecordingSettingsCard domain={tenant.domain} companyName={tenant.name} />}
        {/* Smart messaging: its rules, and legal holds (company admins and Stitch staff). */}
        {s && <SmartMessagesCard domain={tenant.domain} companyName={tenant.name} />}
        {s?.editable && <LegalHoldsCard domain={tenant.domain} companyName={tenant.name} />}
        {s?.editable && <MessagesExportCard domain={tenant.domain} companyName={tenant.name} />}
        {s?.editable && <StorageSummaryCard domain={tenant.domain} companyName={tenant.name} />}
        {s?.editable && companyAdmin && <TextsCard domain={tenant.domain} companyName={tenant.name} />}
        {s?.editable && companyAdmin && <DeletedMessagesCard domain={tenant.domain} companyName={tenant.name} />}
      </div>
    </>
  );
}

/**
 * Voicemail transcripts: off until the company turns them on, then on for each mailbox (a person's,
 * or a shared voicemail box) chosen here, on a person's page or on Voicemail boxes. People turn
 * their own on or off (My phone) only when the company lets them.
 */
function TranscriptsCard({ domain, t, onSaved }: { domain: string; t: NonNullable<CompanySettings['transcripts']>; onSaved: () => void }) {
  const { on, selfService, every } = t;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const people = useData(() => (on ? api.people(domain) : Promise.resolve(null)), [domain, on], { live: false });
  const all = (people.data?.people ?? []).filter((p) => p.extension).sort((a, b) => a.extension.localeCompare(b.extension, undefined, { numeric: true }));
  const onBoxes = all.filter((p) => transcribed(t, p.extension)).map((p) => p.extension);
  const label = (p: (typeof all)[number]) => `${p.firstName} ${p.lastName}`.trim() || p.extension;
  const shown = all.filter((p) => !filter.trim() || `${p.extension} ${label(p)}`.toLowerCase().includes(filter.trim().toLowerCase()));
  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await fn();
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusy(false);
    }
  }
  const count = people.data ? `${onBoxes.length} of ${all.length} ${all.length === 1 ? 'mailbox' : 'mailboxes'}` : every ? 'every mailbox' : `${t.mailboxes.length === 1 ? '1 mailbox' : `${t.mailboxes.length} mailboxes`}`;
  return (
    <Card title="Voicemail transcripts">
      <div className="card-body settings-section">
        <p className="muted settings-lede">
          Stitch writes out voicemail, so it can be read on Voicemail (and in voicemail emails) without playing it. It’s on for every mailbox unless you choose otherwise
          here (or on a person’s page). You choose whether people can turn their own on or off. The audio goes only to Stitch’s own transcription service. A transcript is
          deleted with its voicemail, when its mailbox is turned off, and after 90 days unread. Reading someone else’s transcripts is written to Activity.
        </p>
        {!t.available && (
          <Notice tone="warn" title="Not writing anything out yet">
            Stitch’s transcription service isn’t set up yet, so no voicemail is written out for now, whatever is chosen here. Stitch support is setting it up; your choices
            apply as soon as it’s running.
          </Notice>
        )}
        <label className="check-row">
          <input type="checkbox" checked={on} disabled={busy} onChange={(e) => void run(() => api.saveCompanySettings(domain, { transcripts: e.target.checked }))} />
          <span className="cell-stack">
            <span>Write out voicemail for this company</span>
            <small className="muted">
              {on ? `On for ${count}.` : `Off: voicemail is kept as audio only. Turning it on writes out ${every ? 'every mailbox (except any switched off)' : 'only the mailboxes chosen'}.`}
            </small>
          </span>
        </label>
        {on && (
          <label className="check-row">
            <input type="checkbox" checked={every} disabled={busy} onChange={(e) => void run(() => api.saveCompanySettings(domain, { transcriptsEvery: e.target.checked }))} />
            <span className="cell-stack">
              <span>Every mailbox, including new ones</span>
              <small className="muted">
                {every ? 'Any mailbox you switch off below stays off.' : 'Off: only the mailboxes chosen below. The others’ transcripts were deleted.'}
              </small>
            </span>
          </label>
        )}
        {on && (
          <label className="check-row">
            <input type="checkbox" checked={selfService} disabled={busy} onChange={(e) => void run(() => api.saveCompanySettings(domain, { transcriptsSelf: e.target.checked }))} />
            <span className="cell-stack">
              <span>Let people turn it on or off for their own voicemail</span>
              <small className="muted">{selfService ? 'On My phone, each person can switch theirs.' : 'Off: only admins choose whose voicemail is written out.'}</small>
            </span>
          </label>
        )}
        {on && (
          <div className="transcript-boxes">
            <div className="transcript-boxes-head">
              <input type="search" className="input" placeholder="Find a person or box" aria-label="Find a person or voicemail box" value={filter} onChange={(e) => setFilter(e.target.value)} />
              <Button size="small" disabled={busy || !all.length || onBoxes.length === all.length} onClick={() => void run(() => api.setTranscriptBoxes(domain, all.map((p) => p.extension), true))}>
                Turn on for everyone
              </Button>
              <Button size="small" variant="ghost" disabled={busy || !onBoxes.length} onClick={() => void run(() => api.setTranscriptBoxes(domain, onBoxes, false))}>
                Turn off for everyone
              </Button>
            </div>
            {people.error ? (
              <LoadError error={people.error} retry={people.reload} />
            ) : !people.data ? (
              <Loading label="Loading people and voicemail boxes" />
            ) : (
              <ul className="plain-list transcript-box-list">
                {shown.map((p) => (
                  <li key={p.extension}>
                    <label className="check-row">
                      <input type="checkbox" checked={transcribed(t, p.extension)} disabled={busy} onChange={(e) => void run(() => api.setTranscriptBoxes(domain, [p.extension], e.target.checked))} />
                      <span className="cell-stack">
                        <span>
                          {label(p)} <span className="mono muted">{p.extension}</span>
                        </span>
                        {p.featureSet === MAILBOX_FEATURE_SET && <small className="muted">Shared voicemail box</small>}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Card>
  );
}

/**
 * Texts (1.12.30, Teampace 2956): may people delete texts in Messages? On unless an admin turns it
 * off. A deleted text can be brought back for a day; a legal hold keeps texts whatever this says.
 */
function TextsCard({ domain, companyName }: { domain: string; companyName: string }) {
  const data = useData(() => api.textingSettings(domain), [domain], { live: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const v = data.data;
  async function set(on: boolean) {
    setBusy(true);
    setError('');
    try {
      await api.setTextingSettings(domain, on);
      data.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="Texts">
      <div className="card-body settings-section">
        {data.error ? (
          <LoadError error={data.error} retry={data.reload} />
        ) : !v ? (
          <Loading label="Loading texting settings" />
        ) : (
          <>
            <p className="muted settings-lede">
              Texts go where each number’s calls go, unless you choose otherwise under Texts go to on Numbers (a ring group’s or queue’s people share its texts); texts that would
              reach nobody in Stitch go to {companyName}’s admins. Anyone who sees a conversation can delete a text in it, for everyone who sees it. Deleting doesn’t unsend it: the
              other phone and the carrier keep their copy.
            </p>
            <label className="check-row">
              <input type="checkbox" checked={v.canDelete} disabled={busy || !v.canChange} onChange={(e) => void set(e.target.checked)} />
              <span className="cell-stack">
                <span>People can delete texts</span>
                <small className="muted">
                  {v.canDelete
                    ? 'On: a deleted text can be brought back for a day, then it’s gone from Stitch. Texts under a legal hold can’t be deleted.'
                    : `Off: ${companyName} keeps every text in Stitch.`}
                </small>
              </span>
            </label>
            {error && <Notice tone="crit">{error}</Notice>}
            {/* 1.13.0 (3073): registering for texting, with Leland. */}
            <Registration domain={domain} />
            {/* 1.12.32 (2966): STOP, START and HELP, for the company's admins. */}
            {v.help && <HelpAnswer domain={domain} help={v.help} onSaved={data.reload} />}
            {v.moveTexts && v.canChange && <MoveTexts domain={domain} moveTexts={v.moveTexts} onSaved={data.reload} />}
            {typeof v.optedOut === 'number' && <OptedOut domain={domain} count={v.optedOut} />}
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Deleted chat messages (1.13.0, Teampace 2932): whoever deletes a message in a channel or direct
 * message can bring it back for a day. On unless an admin turns it off; off, a delete is final at once.
 * A legal hold keeps a deleted message (and its files) whatever this says.
 */
function DeletedMessagesCard({ domain, companyName }: { domain: string; companyName: string }) {
  const data = useData(() => api.messageSettings(domain), [domain], { live: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const v = data.data;
  async function set(on: boolean) {
    setBusy(true);
    setError('');
    try {
      await api.setMessageSettings(domain, on);
      data.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="Deleted messages">
      <div className="card-body settings-section">
        {data.error ? (
          <LoadError error={data.error} retry={data.reload} />
        ) : !v ? (
          <Loading label="Loading message settings" />
        ) : (
          <>
            <label className="check-row">
              <input type="checkbox" checked={v.undo} disabled={busy || !v.canChange} onChange={(e) => void set(e.target.checked)} />
              <span className="cell-stack">
                <span>A deleted message can be brought back for a day</span>
                <small className="muted">
                  {v.undo
                    ? 'On: whoever deleted a message in a channel or direct message sees Undo for a day. Then it’s gone from Stitch, with its files.'
                    : `Off: a deleted message is gone from Stitch at once, with its files.`}{' '}
                  While a legal hold covers a message, {companyName} keeps it and its files for the export, whatever this says.
                </small>
              </span>
            </label>
            {error && <Notice tone="crit">{error}</Notice>}
          </>
        )}
      </div>
    </Card>
  );
}

/** Automatic texts are kept to two text messages' worth (the server's MAX_AUTO_TEXT). */
const MAX_HELP = 300;

/** The answer to HELP: what goes out now, and the company's own words (empty: Stitch's). */
function HelpAnswer({ domain, help, onSaved }: { domain: string; help: { text: string | null; shown: string }; onSaved: () => void }) {
  const [text, setText] = useState(help.text ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const own = text.replace(/\s+/g, ' ').trim();
  const over = own.length > MAX_HELP;
  async function save(next: string | null) {
    setBusy(true);
    setError('');
    setSaved('');
    try {
      const v = await api.setTextHelp(domain, next);
      setText(v.help?.text ?? '');
      setSaved(next ? 'Saved. HELP texts get your answer from now on.' : 'Back to Stitch’s answer.');
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="texts-sub" aria-labelledby="help-answer-title">
      <h3 id="help-answer-title">Answer to HELP</h3>
      <p className="muted settings-lede">When someone texts HELP or INFO to one of your numbers, Stitch answers with:</p>
      <p className="tt-bubble help-shown">{help.shown}</p>
      <TextAreaField
        label="Your own answer"
        rows={3}
        value={text}
        placeholder="Empty: Stitch’s answer, above"
        hint="Your company’s name, and a phone number or website to reach you. If it doesn’t say how to stop, “Reply STOP to opt out.” is added."
        error={over ? `Up to ${MAX_HELP} characters.` : undefined}
        onChange={(e) => setText(e.target.value)}
      />
      <span className={`tt-count${over ? ' over' : ''}`}>
        {own.length} / {MAX_HELP} characters
      </span>
      <div className="settings-actions">
        <Button variant="primary" size="small" disabled={busy || over || !own || own === (help.text ?? '')} onClick={() => void save(own)}>
          {busy ? 'Saving…' : 'Save answer'}
        </Button>
        <Button variant="ghost" size="small" disabled={busy || help.text === null} onClick={() => void save(null)}>
          Use Stitch’s answer
        </Button>
      </div>
      {saved && <Notice tone="ok">{saved}</Notice>}
      {error && <Notice tone="crit">{error}</Notice>}
      <p className="texts-rule">
        <Icon name="check" size={15} /> STOP, START and HELP are answered automatically for every number.
      </p>
    </section>
  );
}

/** Storage and retention (Storage A1): what Stitch keeps, the company's own copy, and the way in. */
function StorageSummaryCard({ domain, companyName }: { domain: string; companyName: string }) {
  const view = useData(() => storageApi.view(domain), [domain], { live: false });
  const c = view.data?.connection;
  const chip = !view.data ? null : c ? (c.lastError ? <Chip tone="crit">Not answering</Chip> : <Chip tone="ok">Your storage connected</Chip>) : <Chip>Stitch’s copy only</Chip>;
  return (
    <Card title="Storage and retention" action={chip ?? undefined}>
      <div className="card-body stack">
        <p className="muted settings-lede">
          Stitch archives every one of {companyName}’s calls in open files. Keep a second copy in your own bucket (Amazon S3, Backblaze B2, Wasabi, Cloudflare R2 and others), yours even if you
          leave.
        </p>
        <div className="settings-actions">
          <Link className="btn small primary" to={`/t/${encodeURIComponent(domain)}/settings/storage`}>
            {c ? 'Open storage and retention' : 'Add your own storage'}
          </Link>
        </div>
      </div>
    </Card>
  );
}

/** Texting registration: where it stands, and the way in (Settings → Texting registration). */
function Registration({ domain }: { domain: string }) {
  const reg = useData(() => textingApi.registration(domain), [domain], { live: false });
  const r = reg.data?.registration;
  const words = r ? STATE_WORDS[r.state] : null;
  return (
    <section className="texts-sub" aria-labelledby="texting-reg-title">
      <h3 id="texting-reg-title">
        Texting registration {words && <Chip small tone={words.tone}>{words.label}</Chip>}
      </h3>
      <p className="muted settings-lede">
        Carriers block business texts from numbers that aren’t registered (10DLC). Register once, with Leland’s help; the numbers that text as a team go on it.
      </p>
      <div className="settings-actions">
        <Link className="btn small primary" to={`/t/${domain}/settings/texting`}>
          {r?.state === 'draft' || !r ? 'Register for texting' : 'Open texting registration'}
        </Link>
      </div>
    </section>
  );
}

/** What Move to a team sends: the person's own text, and the team's (open and closed); empty: Stitch's words. */
function MoveTexts({ domain, moveTexts, onSaved }: { domain: string; moveTexts: NonNullable<TextingSettingsView['moveTexts']>; onSaved: () => void }) {
  const [t, setT] = useState({ person: moveTexts.person ?? '', team: moveTexts.team ?? '', closed: moveTexts.closed ?? '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const tidy = (v: string) => v.replace(/\s+/g, ' ').trim() || null;
  async function save(reset = false) {
    setBusy(true);
    setError('');
    setSaved('');
    try {
      const v = await textingApi.setMoveTexts(domain, reset ? { person: null, team: null, closed: null } : { person: tidy(t.person), team: tidy(t.team), closed: tidy(t.closed) });
      setT({ person: v.moveTexts?.person ?? '', team: v.moveTexts?.team ?? '', closed: v.moveTexts?.closed ?? '' });
      setSaved(reset ? 'Back to Stitch’s words.' : 'Saved.');
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="texts-sub" aria-labelledby="move-texts-title">
      <h3 id="move-texts-title">Moving a conversation to a team</h3>
      <p className="muted settings-lede">
        When someone moves a conversation from their own number to a ring group or queue, the contact gets two texts: one from the person, saying which number to expect, and one
        from the team’s number. You can use {'{person}'}, {'{team}'}, {'{company}'}, {'{team number}'} and {'{place}'}.
      </p>
      <TextAreaField label="From the person’s number" rows={2} value={t.person} placeholder={moveTexts.defaults.person} onChange={(e) => setT({ ...t, person: e.target.value })} />
      <TextAreaField label="From the team’s number" rows={2} value={t.team} placeholder={moveTexts.defaults.team} onChange={(e) => setT({ ...t, team: e.target.value })} />
      <TextAreaField label="From the team’s number, while it’s closed" rows={2} value={t.closed} placeholder={moveTexts.defaults.closed} onChange={(e) => setT({ ...t, closed: e.target.value })} />
      <div className="settings-actions">
        <Button variant="primary" size="small" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save texts'}
        </Button>
        <Button variant="ghost" size="small" disabled={busy || (!moveTexts.person && !moveTexts.team && !moveTexts.closed)} onClick={() => void save(true)}>
          Use Stitch’s words
        </Button>
      </div>
      <small className="muted">The team’s text is one of your registered samples: if you change it a lot, update the campaign too.</small>
      {saved && <Notice tone="ok">{saved}</Notice>}
      {error && <Notice tone="crit">{error}</Notice>}
    </section>
  );
}

/** Who opted out of the company's texts: newest first, the first 20 until Show all, and every STOP and START as a CSV file. */
function OptedOut({ domain, count }: { domain: string; count: number }) {
  const list = useData(() => (count > 0 ? api.optOuts(domain) : Promise.resolve(null)), [domain, count], { live: false });
  const [all, setAll] = useState(false);
  const people = [...(list.data?.optedOut ?? [])].sort((a, b) => b.changedAt.localeCompare(a.changedAt));
  const shown = all ? people : people.slice(0, 20);
  return (
    <section className="texts-sub" aria-labelledby="opted-out-title">
      <h3 id="opted-out-title">
        Opted out <Chip small>{count}</Chip>
      </h3>
      <p className="muted settings-lede">
        {count === 0 ? 'Nobody has opted out of your texts. ' : count === 1 ? '1 person gets no texts from your numbers. ' : `${count} people get no texts from your numbers. `}
        Texting STOP (or a word like it) to any of your numbers stops texts from all of them, until they text START. Nobody at your company can undo it for them.
      </p>
      {count > 0 &&
        (list.error ? (
          <LoadError error={list.error} retry={list.reload} />
        ) : !list.data ? (
          <Loading label="Loading who opted out" />
        ) : (
          <ul className="opt-out-list" aria-label="Opted out">
            {shown.map((o) => (
              <li key={o.otherNumber}>
                <strong className="mono">{formatPhoneNumber(o.otherNumber)}</strong>
                <span>
                  texted <Chip small>{o.keyword || 'STOP'}</Chip> to <span className="mono">{formatPhoneNumber(o.viaNumber)}</span>
                </span>
                <span className="muted opt-out-when">{shortWhen(o.changedAt)}</span>
              </li>
            ))}
          </ul>
        ))}
      <div className="settings-actions opt-out-actions">
        {people.length > 20 && (
          <Button size="small" variant="ghost" onClick={() => setAll(!all)}>
            {all ? 'Show the first 20' : `Show all ${people.length}`}
          </Button>
        )}
        <a className="btn small secondary" href={api.optOutsCsvUrl(domain)} download>
          <Icon name="download" size={15} strokeWidth={2.2} /> Download CSV
        </a>
        <span className="muted small">Every STOP and START, with when and which number, for audits.</span>
      </div>
    </section>
  );
}

/** The time now in a zone: "3:46 PM". */
function clockTimeIn(tz: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: tz }).format(new Date());
}
